import React, { useCallback, useMemo, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import BlockIcon from '@material-ui/icons/Block';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import SendIcon from '@material-ui/icons/Send';

import { canManageReportCards, useReportCardAvailability } from '../../lib/services/report-card-access.service';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { OutlineButton, PrimaryButton } from '../_core/_ui/buttons.component';
import { DashboardCard } from '../_core/_ui/dashboard-card.component';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { athleteNouns } from './report-card-access.helpers';
import { getMessages as getStatusMessages } from './report-card-access.messages';
import { ReportCardAgeGroupAccessDialog } from './report-card-age-group-access-dialog.component';
import { useAgeGroupReportCardAccess } from './report-card-age-group.use-access';
import { ReportCardAvailabilitySummary } from './report-card-availability-summary.component';
import { useReportCardAvailabilityStyles } from './report-card-availability.styles';
import { SendReportCardsDialog } from './send-report-cards-dialog.component';

interface IReportCardAvailabilityPanelProps {
  ageGroup: IAgeGroup;
}

// The age group's report card status for REPORTS staff; sending and turning access off or on need
// CONFIGURATION on the age group, which the server requires for each.
export const ReportCardAvailabilityPanel: React.FC<IReportCardAvailabilityPanelProps> = ({ ageGroup }) => {
  const classes = useReportCardAvailabilityStyles();
  const termLookup = useCompanySportTerminology();
  const statusMessages = useMemo(() => getStatusMessages(termLookup), [termLookup]);
  const nouns = useMemo(() => athleteNouns(termLookup), [termLookup]);
  const canManage = canManageReportCards(ageGroup._id);
  const { availability, error } = useReportCardAvailability(ageGroup);
  const access = useAgeGroupReportCardAccess(ageGroup, availability);
  const { messages } = access;
  const [sendOpen, setSendOpen] = useState(false);

  const openSend = useCallback(() => setSendOpen(true), []);
  const closeSend = useCallback(() => setSendOpen(false), []);
  const { request } = access;
  const requestRevoke = useCallback(() => request('REVOKE'), [request]);
  const requestReEnable = useCallback(() => request('RE_ENABLE'), [request]);

  const revoked = !!availability?.revokedAt;

  return (
    <DashboardCard>
      <div className={classes.header}>
        <Typography variant="h6"><IntlMsg {...messages.titlePanel} /></Typography>
        {access.inFlight && <CircularProgress size={18} />}
        {canManage && availability && (
          <div className={classes.actions}>
            {revoked ? (
              <OutlineButton startIcon={<LockOpenIcon />} disabled={access.busy} onClick={requestReEnable}>
                <IntlMsg {...messages.btnReEnableAgeGroup} />
              </OutlineButton>
            ) : (
              <OutlineButton startIcon={<BlockIcon />} disabled={access.busy} onClick={requestRevoke}>
                <IntlMsg {...messages.btnRevokeAgeGroup} />
              </OutlineButton>
            )}
            <PrimaryButton endIcon={<SendIcon />} disabled={access.busy} onClick={openSend}>
              <IntlMsg {...messages.btnSend} />
            </PrimaryButton>
          </div>
        )}
      </div>

      {availability && (
        <ReportCardAvailabilitySummary availability={availability} messages={messages} statusMessages={statusMessages} nouns={nouns} />
      )}
      {!availability && error && (
        <Typography variant="body2" color="error"><IntlMsg {...messages.errAvailability} /></Typography>
      )}
      {!availability && !error && <CircularProgress size={24} />}

      {availability && (
        <ReportCardAgeGroupAccessDialog
          action={access.confirming}
          ageGroupName={ageGroup.name}
          counts={availability.counts}
          messages={messages}
          nouns={nouns}
          onConfirm={access.confirm}
          onCancel={access.cancel}
        />
      )}
      {canManage && availability && (
        <SendReportCardsDialog
          open={sendOpen}
          ageGroup={ageGroup}
          availability={availability}
          messages={messages}
          nouns={nouns}
          onClose={closeSend}
          onReEnable={access.reEnable}
          reEnabling={access.inFlight}
        />
      )}
    </DashboardCard>
  );
};
